// ═══════════════════════════════════════════════════════════════
// HELP ARTICLES — Edit this file to add or update help content
// ═══════════════════════════════════════════════════════════════
// Each article needs: id (slug), category, title, lastUpdated, readMins, sections
// Sections are an array of blocks. Block types:
//   { type: "p", text: "..." }                   ← paragraph
//   { type: "h2", text: "..." }                  ← subheading
//   { type: "list", items: ["...", "..."] }      ← bullet list
//   { type: "info", title: "...", text: "..." }  ← info callout box (blue)
//   { type: "warn", title: "...", text: "..." }  ← warning callout box (orange)
//   { type: "kbd", keys: ["G", "Ctrl+Z"] }       ← keyboard shortcut row
//   { type: "kbdRow", key: "0", desc: "..." }    ← two-column shortcut row
//
// Article order within a category drives sidebar order.
// Categories appear in CATEGORY_ORDER below.
//
// NOTE: the sidebar search filters on article TITLE only, so keep the words
// a user would actually type in the title (e.g. "3D", "PDF", "shortcuts").

export const CATEGORY_ORDER = [
  "Getting started",
  "Designing layouts",
  "3D & simulation",
  "AI features",
  "Exporting",
];

// Emoji per category — used by HelpPage sidebar
export const CATEGORY_ICONS = {
  "Getting started": "🚀",
  "Designing layouts": "📐",
  "3D & simulation": "🧊",
  "AI features": "✨",
  "Exporting": "📄",
};

export const HELP_ARTICLES = [
  // ─── Getting started ─────────────────────────────────────────
  {
    id: "welcome",
    category: "Getting started",
    title: "Welcome to ShopBuilder Pro",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "ShopBuilder Pro helps you design professional automotive shop layouts in minutes. Set your building dimensions, drag equipment into place, walk the result in 3D, and export client-ready drawings — all in your browser.",
      },
      { type: "h2", text: "The main pages" },
      {
        type: "p",
        text: "Setup — define shop dimensions, draw the building shape, place doors, and name the project. Open Floor Plan takes you to the next step.",
      },
      {
        type: "p",
        text: "Floor plan — the design canvas. Drag equipment from the left sidebar, arrange it, add rooms and drains, and flip to the 3D view. Configure goes back to Setup; Export Layout moves forward.",
      },
      {
        type: "p",
        text: "Export — fill in customer and site details, pick real brands and models for each unit, and download a PNG or a professional PDF drawing set with a title block, equipment schedule and notes.",
      },
      { type: "h2", text: "The ⋮ menu" },
      {
        type: "p",
        text: "The three-dot button at the far right of the top bar holds AI Options (Describe Layout, Import Sketch, Analyze Layout), What's New, Help & Docs, and About. If you are hunting for a feature that used to be a top-bar button, it is almost certainly in there.",
      },
      {
        type: "info",
        title: "Newest features",
        text: "v3.0 added the 3D architectural sketch view. v4.0 added blueprint exports, a real 137-model brand catalog, and AI order summaries. v4.1 added vehicle approach simulation in both 2D and 3D. Open What's New from the ⋮ menu for the full history.",
      },
    ],
  },
  {
    id: "shop-dimensions",
    category: "Getting started",
    title: "Setting shop dimensions",
    lastUpdated: "September 2026",
    readMins: 3,
    sections: [
      {
        type: "p",
        text: "On the Setup page you choose between two shop shape modes: Rectangle (default) and Free-form. Pick the one that matches your building.",
      },
      { type: "h2", text: "Rectangle mode" },
      {
        type: "p",
        text: "Enter width and height in feet or metres. The shape preview updates as you type. This is the fastest way to start when your shop is a simple box.",
      },
      { type: "h2", text: "Free-form mode" },
      {
        type: "p",
        text: "Click in the drawing canvas to place wall corners. Each click adds a vertex and starts the next wall from there. Click your starting point to close the shape. You need at least three walls to form a valid shop.",
      },
      { type: "h2", text: "Drawing aids" },
      {
        type: "list",
        items: [
          "The mouse snaps to whole feet — hold Alt for fine positioning",
          "Walls snap to 0°, 45° and 90° — hold Shift to draw at any angle",
          "Pink alignment guides appear when a corner lines up with an existing one",
          "Type a number while drawing to open the length panel and enter an exact wall length",
          "Drag any corner to move it, before or after the shape is closed",
          "Press F to fit the drawing to the canvas — same as the floating Fit button",
          "Backspace removes the last corner you placed",
        ],
      },
      {
        type: "warn",
        title: "Switching modes loses your work",
        text: "If you have drawn a free-form shape and switch back to Rectangle, your custom shape is discarded. The app asks you to confirm first.",
      },
    ],
  },
  {
    id: "doors-walls",
    category: "Getting started",
    title: "Adding doors and walls",
    lastUpdated: "September 2026",
    readMins: 3,
    sections: [
      { type: "h2", text: "In rectangle mode" },
      {
        type: "p",
        text: "Use the NORTH / SOUTH / EAST / WEST tabs to switch between walls. For each wall you can add walk doors and overhead doors with custom widths and positions.",
      },
      { type: "h2", text: "In free-form mode" },
      {
        type: "p",
        text: "Click any wall in your drawn shape to add a walk door at the midpoint. To add an overhead door instead, use the door type selector before clicking. Drag any door along its wall to reposition it — doors stay attached to their wall even if you edit the shape later.",
      },
      {
        type: "info",
        title: "Door colours are standardised",
        text: "Walk doors are green, overhead doors are yellow. These colours match standard architectural drawings and are not affected by theme changes.",
      },
      {
        type: "info",
        title: "Overhead doors drive the vehicle simulation",
        text: "The approach simulation always starts a vehicle at an overhead door. A shop with no overhead door gives the simulation nowhere to drive in from — see Simulating a vehicle approach.",
      },
      {
        type: "info",
        title: "No compass in free-form",
        text: "The N/S/E/W compass marker only appears in rectangle mode. Free-form shops do not have fixed wall orientations, so the compass is hidden to avoid confusion.",
      },
    ],
  },
  {
    id: "saving-projects",
    category: "Getting started",
    title: "Saving and loading projects",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "Save Project downloads your work as a .sbpro file. Load Project opens one back up. The file holds the shop shape, doors, rooms, drains, every piece of equipment, the brand and model choices you have made, and any dimensions you have drawn.",
      },
      { type: "h2", text: "How files are named" },
      {
        type: "p",
        text: "Saving is one click — there is no pop-up asking for a name. The file is named after your project plus a timestamp, so repeated saves never overwrite each other. Set the project name at the top of the Setup page and it flows through to the file name, the Export page, and the 3D view header. Projects saved without a name fall back to Untitled Project.",
      },
      { type: "h2", text: "Tips for working with saved files" },
      {
        type: "list",
        items: [
          "Keep one project file per customer so quotes do not get mixed up",
          "Save before exporting — exports do not update the saved file",
          ".sbpro files are portable: email them between machines or share with a colleague",
        ],
      },
      {
        type: "info",
        title: "Older projects load fine",
        text: "The file picker accepts both .sbpro and the older .json saves, so nothing you saved before is left behind. Projects saved before free-form shapes existed also migrate automatically — legacy N/S/E/W doors are converted to the wall-anchored format the first time you open them.",
      },
      {
        type: "warn",
        title: "Loading replaces the current layout",
        text: "Loading a project wipes whatever is on the canvas, including the undo history. Save your current work first if you do not want to lose it.",
      },
    ],
  },
  {
    id: "signing-in",
    category: "Getting started",
    title: "Signing in and what it unlocks",
    lastUpdated: "September 2026",
    readMins: 1,
    sections: [
      {
        type: "p",
        text: "Everything that draws, measures, and exports works without an account. Sign-in is only required for the features that call the AI service.",
      },
      { type: "h2", text: "What needs an account" },
      {
        type: "list",
        items: [
          "Describe Layout — plain-English layout generation",
          "Import Sketch — hand-drawn plan conversion",
          "Analyze Layout — AI review of the current plan",
          "Generate Summary — the AI order summary on the Export page",
        ],
      },
      {
        type: "info",
        title: "Spotting a locked button",
        text: "Locked buttons show a 🔒 and a Sign in to use this feature tooltip. Clicking one opens the sign-in window in place — you do not lose your layout, and you land back on the same button when you are done.",
      },
    ],
  },

  // ─── Designing layouts ───────────────────────────────────────
  {
    id: "drag-drop",
    category: "Designing layouts",
    title: "Adding equipment to the canvas",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "The left sidebar groups equipment by category: Lifts, Tire, Air, Service, Storage, and Structure. Drag any item onto the canvas to place it. The item drops at your cursor position and snaps to the grid automatically.",
      },
      { type: "h2", text: "Categories at a glance" },
      {
        type: "list",
        items: [
          "Lifts — 2-post, 4-post, scissor, in-ground, and mobile column",
          "Tire — tire changer, wheel balancer, alignment rack",
          "Air — compressor",
          "Service — wash bay, floor jack, oil drain",
          "Storage — toolbox, workbench",
          "Structure — generic obstructions like support columns",
        ],
      },
      { type: "h2", text: "Rooms, drains, and doors" },
      {
        type: "p",
        text: "Rooms, floor drains, and doors are added from buttons in the sidebar rather than the equipment library. They drag, select, and rotate like equipment but have their own properties — see Rooms, floor drains and labels.",
      },
      {
        type: "info",
        title: "Wireframe images",
        text: "Lifts and tire equipment show realistic wireframe drawings on the canvas. Other categories show simple rectangles with labels — equally functional, just less detailed. Most equipment also has a detailed model in the 3D view.",
      },
    ],
  },
  {
    id: "navigating-canvas",
    category: "Designing layouts",
    title: "Navigating the canvas (zoom, pan, fit)",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "The canvas supports unlimited zoom and pan so you can work at any scale, from the whole shop down to fine details around a single bay.",
      },
      { type: "h2", text: "Zooming" },
      {
        type: "list",
        items: [
          "Scroll wheel — zooms in or out toward your cursor",
          "Toolbar buttons − and + at the bottom-right of the canvas — zoom in fixed steps",
          "The percentage between the buttons shows your current zoom level",
        ],
      },
      { type: "h2", text: "Panning" },
      {
        type: "p",
        text: "Hold Alt and drag anywhere on the canvas to pan around. The cursor changes to a hand to confirm pan mode.",
      },
      { type: "h2", text: "Fit to screen" },
      {
        type: "p",
        text: "Press 0 (zero) at any time to fit the entire shop to the visible canvas area. There is also a ⊡ Fit button in the bottom-right toolbar, plus a floating Fit to Screen button that appears in the top-right corner whenever you are zoomed or panned away from the default view.",
      },
      {
        type: "info",
        title: "Exports fit themselves now",
        text: "Since v4.0, PNG and PDF exports always fit and centre the whole shop regardless of the zoom or pan you left the editor at. Fitting first is still a good habit for screenshots, but your export will no longer come out cropped.",
      },
    ],
  },
  {
    id: "selecting-editing",
    category: "Designing layouts",
    title: "Selecting and editing equipment",
    lastUpdated: "September 2026",
    readMins: 3,
    sections: [
      {
        type: "p",
        text: "Click any item to select it. Selected items show resize handles at the four corners and a rotation arc above. The right sidebar updates to show position, size, and rotation controls for whatever you have selected.",
      },
      { type: "h2", text: "Multi-select" },
      {
        type: "list",
        items: [
          "Shift+click items one at a time to add them to the selection",
          "Click and drag on empty canvas to draw a selection box — anything inside it gets selected",
          "Once 2+ items are selected, alignment tools (Left, Center, Right, Top, Middle, Bottom) appear in the sidebar",
          "With 3+ items selected, distribute tools appear (space evenly horizontally or vertically)",
          "Press Escape or click empty canvas to deselect",
        ],
      },
      { type: "h2", text: "Moving, rotating, resizing" },
      {
        type: "p",
        text: "Drag a selected item to move it; with several selected, dragging any one moves the group. Drag the dashed cyan arc above an item to rotate it freely, with the angle shown as you go. Drag a corner handle to resize, or type exact width and depth values in the sidebar when a single item is selected.",
      },
      { type: "h2", text: "Locking items" },
      {
        type: "p",
        text: "Press L to lock or unlock everything in the current selection. Locked items refuse to delete — if you try, a message tells you how many were skipped and reminds you to press L. Lock a bay you have finalised before rearranging everything around it. Pasted copies always start unlocked.",
      },
      { type: "h2", text: "Copy, paste, delete" },
      {
        type: "list",
        items: [
          "Ctrl+C copies the current selection",
          "Ctrl+V pastes a copy offset slightly from the original",
          "Delete or Backspace removes selected items, unless they are locked",
        ],
      },
      {
        type: "info",
        title: "Naming a unit",
        text: "A selected item has a Bay / Custom Label field in the sidebar. Whatever you type there follows the item into the 3D floor labels and the exported equipment list — Bay 1, Alignment, Wash Bay, and so on.",
      },
    ],
  },
  {
    id: "rooms-drains-labels",
    category: "Designing layouts",
    title: "Rooms, floor drains and labels",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "Rooms and drains are structural features rather than equipment, so they have their own buttons in the sidebar and their own properties.",
      },
      { type: "h2", text: "Rooms" },
      {
        type: "p",
        text: "Add a rectangular room for a simple office or parts room, or draw a polygon room by clicking corners on the canvas when the space is an odd shape. Each room has a name and a colour. Press Escape while drawing to cancel, or Backspace to remove the last corner.",
      },
      { type: "h2", text: "Floor drains" },
      {
        type: "p",
        text: "Drains come in two shapes — long trench drains and square catch basins. Place them, then drag or resize like anything else. Click a drain to select it and press Delete to remove it.",
      },
      {
        type: "info",
        title: "Both appear in 3D",
        text: "Rooms project as half-height partition walls in the 3D view, so enclosed spaces read clearly without blocking the camera. Drains render as dark rectangles with grate slats. Room names and equipment labels bake into the 3D floor and can be toggled with the Labels button.",
      },
    ],
  },
  {
    id: "canvas-toolbar",
    category: "Designing layouts",
    title: "Canvas toolbar (snap, grid, measure)",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "The thin bar at the bottom of the canvas holds tools that change how the canvas behaves. Each is a toggle — clicking it turns the mode on or off.",
      },
      { type: "h2", text: "Snap size" },
      {
        type: "p",
        text: "Choose how movement snaps: None (free placement), 3 inches, 6 inches, 1 foot, or 2 feet. Smaller snap sizes give precise placement; larger ones force everything onto a clean grid. 1 foot is a good default for most shop work.",
      },
      { type: "h2", text: "Grid toggle" },
      {
        type: "p",
        text: "Hides or shows the background grid. The keyboard shortcut G does the same thing. Hiding the grid is useful when taking screenshots or reviewing a layout without visual clutter — it does not affect snap behaviour.",
      },
      { type: "h2", text: "Measurements and door labels" },
      {
        type: "p",
        text: "Press M to hide or show the exterior wall measurements and door offset labels, and D to hide or show door labels, widths, and drag handles. Both preferences are stored with the project, so a layout saved with a clean canvas opens that way. Use them to strip the drawing back before a screenshot.",
      },
      { type: "h2", text: "Measure (dimension tool)" },
      {
        type: "p",
        text: "Click Measure to enter dimension mode (a green banner confirms you are in measure mode). Click two points to draw a measurement line between them. The distance label shows in feet and inches. Drag the offset handle on the dimension to move the label away from the measured edge.",
      },
      {
        type: "list",
        items: [
          "You can measure between any two points — walls, equipment corners, doorways",
          "Click an existing dimension to select it, then press Delete to remove",
          "Press Escape to exit measure mode without placing a dimension",
        ],
      },
      {
        type: "info",
        title: "Dimension colour is standardised",
        text: "Dimension lines are always orange. This matches standard architectural drawing conventions and is not affected by theme settings.",
      },
    ],
  },
  {
    id: "bay-clearance-zones",
    category: "Designing layouts",
    title: "Bay clearance zones",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "Bay clearance zones show the real-world working space each lift needs. The zone renders as an amber hatched rectangle around the lift footprint, sized to manufacturer spec so you can spot tight spacing or door conflicts before they become problems on installation day.",
      },
      { type: "h2", text: "Showing or hiding zones" },
      {
        type: "list",
        items: [
          "Click the Bay Zones button in the canvas toolbar to toggle for all lifts at once",
          "Or press B on the keyboard — same thing, faster",
          "When a single lift is selected, a small button appears top-left of the canvas to override that one lift only",
          "The 3D view has its own Bay Zones button, independent of the 2D toggle",
        ],
      },
      { type: "h2", text: "Per-lift override (Auto / On / Off)" },
      {
        type: "p",
        text: "The per-lift button cycles between three states. Auto follows the global toggle. On forces the zone visible regardless of the global setting. Off hides the zone for that lift only. Useful when you want to highlight one bay's clearance in a presentation, or hide a zone that is obscuring nearby equipment.",
      },
      {
        type: "info",
        title: "Default zone size",
        text: "Two-post and in-ground lifts default to a 13 × 26 ft zone (matching the Rotary SPO12RA spec). The zone rotates with the lift — rotate a lift 90° and the zone rotates with it. Equipment without a defined bay footprint will not show a zone.",
      },
    ],
  },
  {
    id: "undo-redo",
    category: "Designing layouts",
    title: "Undo and redo",
    lastUpdated: "September 2026",
    readMins: 1,
    sections: [
      {
        type: "p",
        text: "Made a mistake? Press Ctrl+Z to undo. Ctrl+Y (or Ctrl+Shift+Z) redoes. The undo history covers placement, deletion, moves, rotation, resizing, locking, brand and model choices, and dimension changes — pretty much everything that modifies the layout.",
      },
      {
        type: "info",
        title: "Undo clears the selection",
        text: "Each undo or redo deselects everything, because the items you had selected may no longer exist. This is deliberate — it avoids the app pointing at something that has been undone away.",
      },
      {
        type: "warn",
        title: "These actions clear the undo history",
        text: "Loading a saved project or importing a sketch replaces the entire layout and wipes the undo stack. You cannot undo back across one of those events.",
      },
    ],
  },
  {
    id: "keyboard-shortcuts",
    category: "Designing layouts",
    title: "Keyboard shortcuts (full reference)",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "Quick reference for every keyboard shortcut. Shortcuts work whenever the canvas has focus and you are not typing in a text field — click empty canvas first if one is not responding.",
      },
      { type: "h2", text: "View" },
      { type: "kbdRow", key: "0", desc: "Fit shop to screen" },
      { type: "kbdRow", key: "G", desc: "Toggle grid on/off" },
      { type: "kbdRow", key: "B", desc: "Toggle bay clearance zones" },
      { type: "kbdRow", key: "M", desc: "Toggle wall measurements + door offsets" },
      { type: "kbdRow", key: "D", desc: "Toggle door labels + handles" },
      { type: "kbdRow", key: "Scroll wheel", desc: "Zoom toward cursor" },
      { type: "kbdRow", key: "Alt + Drag", desc: "Pan canvas" },
      { type: "h2", text: "Selection" },
      { type: "kbdRow", key: "Click", desc: "Select item" },
      { type: "kbdRow", key: "Shift + Click", desc: "Add to selection" },
      { type: "kbdRow", key: "Drag empty canvas", desc: "Selection box" },
      { type: "kbdRow", key: "Escape", desc: "Deselect / cancel current action" },
      { type: "h2", text: "Editing" },
      { type: "kbdRow", key: "L", desc: "Lock / unlock selection" },
      { type: "kbdRow", key: "Ctrl + C", desc: "Copy selection" },
      { type: "kbdRow", key: "Ctrl + V", desc: "Paste" },
      { type: "kbdRow", key: "Delete", desc: "Remove selected (locked items are kept)" },
      { type: "kbdRow", key: "Backspace", desc: "Remove selected / last corner while drawing" },
      { type: "kbdRow", key: "Ctrl + Z", desc: "Undo" },
      { type: "kbdRow", key: "Ctrl + Y", desc: "Redo" },
      { type: "kbdRow", key: "Ctrl + Shift + Z", desc: "Redo (alternate)" },
      { type: "h2", text: "Setup page — drawing a free-form shape" },
      { type: "kbdRow", key: "F", desc: "Fit drawing to canvas" },
      { type: "kbdRow", key: "Alt (hold)", desc: "Disable foot snapping" },
      { type: "kbdRow", key: "Shift (hold)", desc: "Disable 45° angle snapping" },
      { type: "kbdRow", key: "0-9", desc: "Open the type-a-length panel" },
      {
        type: "info",
        title: "No rotation shortcut",
        text: "Rotation is done by dragging the cyan arc above a selected item, or via the rotation buttons in the sidebar. There is no keyboard shortcut for it yet.",
      },
      {
        type: "info",
        title: "Drawing colour legend",
        text: "Walls are cyan, dimensions are orange, the north marker is red, walk doors are green, overhead doors are yellow, and bay clearance zones are amber. These follow standard architectural conventions and stay constant regardless of theme.",
      },
    ],
  },

  // ─── 3D & simulation ─────────────────────────────────────────
  {
    id: "3d-view",
    category: "3D & simulation",
    title: "Using the 3D view",
    lastUpdated: "September 2026",
    readMins: 3,
    sections: [
      {
        type: "p",
        text: "The floating 2D / 3D View pill at the bottom of the canvas flips between the editing canvas and an architectural sketch of the same shop. Walls extrude to full height with real door cutouts, overhead doors render as translucent blue glass, and equipment appears as detailed line-art models. It works with free-form shops, rooms, and bay zones intact.",
      },
      { type: "h2", text: "Moving the camera" },
      {
        type: "list",
        items: [
          "Drag to orbit around the shop",
          "Scroll to zoom",
          "Double-click any piece of equipment to focus the camera on it",
          "Reset View snaps back to the whole shop",
        ],
      },
      { type: "h2", text: "The button strip" },
      {
        type: "list",
        items: [
          "Labels — floor-baked names under each unit and inside each room, on by default",
          "Bay Zones — clearance hatching on lifts, separate from the 2D toggle",
          "Reset View — return the camera to the full shop",
          "The camera button saves a PNG of the current 3D view",
        ],
      },
      {
        type: "p",
        text: "Walls are deliberately transparent so you can see inside from any angle, and the project name is baked into the floor above the shop, mirroring the ShopBuilder Pro watermark on the grid below.",
      },
      {
        type: "warn",
        title: "If the view pauses",
        text: "The 3D scene loads on demand and pauses itself if the browser drops the graphics context — usually GPU pressure on a laptop with a lot of tabs open. Click Reload 3D to bring it back. Closing other heavy tabs helps if it keeps happening.",
      },
      {
        type: "info",
        title: "Selection is shared",
        text: "Clicking equipment in 3D selects it, the same as clicking it in 2D. Flip to 3D, spot the unit that is wrong, click it, and flip back with it already selected.",
      },
    ],
  },
  {
    id: "vehicle-approach",
    category: "3D & simulation",
    title: "Simulating a vehicle approach",
    lastUpdated: "September 2026",
    readMins: 4,
    sections: [
      {
        type: "p",
        text: "Approach simulation answers the question a floor plan cannot: can a vehicle actually get onto that lift? Select a 2-post, 4-post, scissor, in-ground, or mobile column lift and click Simulate Approach at the left edge of the canvas. A vehicle drives in from an overhead door, in 2D and 3D, kept in sync.",
      },
      { type: "h2", text: "Setting it up" },
      {
        type: "list",
        items: [
          "Vehicle — car/sedan, SUV, standard or dually pickup (standard or long-box crew cab), van, small shuttle bus, or full-size bus, each with real length, width, and turning radius",
          "Door — defaults to the nearest overhead door; pick a specific one from the dropdown to test a different route",
          "Speed — 0.25x to 3x, shared between the 2D and 3D views",
          "▶ Play approach runs it",
        ],
      },
      { type: "h2", text: "Reading the result" },
      {
        type: "p",
        text: "Auto mode reports how many forward and reverse corrections the vehicle needs to square up on the lift. Zero or one correction is a comfortable bay. A high count means a driver will be shuffling back and forth every single time — worth moving something before the concrete is cut. If there is no workable route at all, the panel says Blocked and gives the reason.",
      },
      {
        type: "p",
        text: "The solver tries nose-in first, then backing through the door and driving forward, and will route through a shared aisle when a neighbouring lift blocks the direct line.",
      },
      { type: "h2", text: "Manual mode" },
      {
        type: "p",
        text: "When the automatic search cannot find a route you know exists, switch to Manual and build the path yourself. Add point drops a waypoint between the door and the lift; drag the dot to move it and the small handle to set its heading. Each point has an FWD/REV button that sets the gear for the leg arriving at it, and Flip lift end swaps which way the vehicle ends up facing on the lift.",
      },
      {
        type: "info",
        title: "Manual paths warn but do not block",
        text: "A manual path that clips something still plays, with a warning. That is intentional — you may be modelling a tight-but-real manoeuvre the collision check is too conservative about. Auto mode is stricter and simply refuses.",
      },
      {
        type: "warn",
        title: "This is a planning aid, not a guarantee",
        text: "Approach simulation is in beta and uses nominal vehicle dimensions and turning radii. Real drivers, mirrors, open doors, and a slippery floor all differ. Verify tight bays on site before committing to a layout.",
      },
    ],
  },

  // ─── AI features ─────────────────────────────────────────────
  {
    id: "describe-layout",
    category: "AI features",
    title: "Describing a layout in plain English",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "Open the ⋮ menu at the far right of the top bar, choose AI Options, then Describe Layout. Type what you want in plain language — for example: three 2-post lifts on the south wall, a tire changer and balancer near the west door, and a workbench in the corner.",
      },
      {
        type: "p",
        text: "The AI places the equipment for you. You can then adjust positions manually with drag and drop.",
      },
      {
        type: "info",
        title: "Sign-in required",
        text: "Describe Layout, Import Sketch, and Analyze Layout all need an account. A locked button shows a 🔒 and opens the sign-in window when clicked.",
      },
      {
        type: "warn",
        title: "Always review AI placements",
        text: "AI is a starting point, not a final answer. Check that bay spacing, door clearances, and lift positions make sense for your specific shop before exporting.",
      },
    ],
  },
  {
    id: "import-sketch",
    category: "AI features",
    title: "Importing a hand-drawn sketch",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "If a customer sends you a hand-drawn floor plan or a photo of a whiteboard, you can convert it to a ShopBuilder layout. Open the ⋮ menu, choose AI Options, then Import Sketch, and upload the image.",
      },
      {
        type: "p",
        text: "The AI identifies walls, doors, and equipment in the image, then places them on a fresh canvas. From there it works like any other layout, including free-form shapes for buildings that are not rectangles.",
      },
      {
        type: "info",
        title: "Image quality matters",
        text: "Clear, well-lit sketches with labelled equipment work best. Blurry photos or messy handwriting may give unreliable results.",
      },
      {
        type: "warn",
        title: "Importing replaces your layout",
        text: "A sketch import starts a fresh canvas and clears the undo history. Save the current project first if you want to keep it.",
      },
    ],
  },
  {
    id: "analyze-layout",
    category: "AI features",
    title: "Analyzing a layout for problems",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "Analyze Layout reviews the plan you have already drawn and reports back on what it finds. Open the ⋮ menu, choose AI Options, then Analyze Layout. Treat it as a second opinion before a drawing goes to a customer, not a replacement for your own review.",
      },
      { type: "h2", text: "What comes back" },
      {
        type: "p",
        text: "Findings are grouped by category — Clearance, Safety, Workflow, Code, and Layout — and sorted by severity, with critical items first, then warnings, then informational notes.",
      },
      {
        type: "warn",
        title: "Treat findings as prompts, not verdicts",
        text: "The analysis is generated by a language model. It can miss real problems and raise things that are fine in your jurisdiction or your customer's workflow. Nothing it says replaces manufacturer specs, local code, or a site visit.",
      },
    ],
  },

  // ─── Exporting ───────────────────────────────────────────────
  {
    id: "export-formats",
    category: "Exporting",
    title: "Choosing an export format (PNG or PDF)",
    lastUpdated: "October 2026",
    readMins: 3,
    sections: [
      {
        type: "p",
        text: "The Download drawing button at the bottom of the Export page offers two formats. Both fit and centre the whole shop automatically, whatever zoom you left the editor at. The preview on the right is exactly what the PDF will look like.",
      },
      { type: "h2", text: "The two formats" },
      {
        type: "list",
        items: [
          "Download PNG — a flat image. Best for pasting into an email, a text message, or a slide",
          "Download PDF drawing — professional drawing sheets in vector, so you can zoom right in on busy layouts. Best for customers, contractors and printing",
        ],
      },
      { type: "h2", text: "Templates and paper" },
      {
        type: "list",
        items: [
          "Modern Presentation — soft fills with each item's name on the plan. The default, and the best fit for customer meetings",
          "Technical Blueprint — crisp line work with EQ-01 style reference tags that match the equipment schedule. Best for contractor handoff",
          "Paper sizes from Letter up to ARCH D, always landscape. Bigger paper gives the floor plan more room",
        ],
      },
      { type: "h2", text: "Colour or black and white" },
      {
        type: "p",
        text: "The Export Mode toggle switches between Colour and Black & White, for both the PNG and the PDF. Black and white is the safer choice for anything going to a shop printer or a photocopier.",
      },
      { type: "h2", text: "Fill in the details first" },
      {
        type: "p",
        text: "The panels above the export button feed the drawing's title block: customer name and contact, project name and notes, site conditions (ceiling height, concrete thickness and condition), and who drew it. Site conditions are also what the AI order summary checks against, so they are worth filling in even for a rough draft.",
      },
    ],
  },
  {
    id: "equipment-list",
    category: "Exporting",
    title: "Equipment list, brands and models",
    lastUpdated: "September 2026",
    readMins: 3,
    sections: [
      {
        type: "p",
        text: "The Equipment List panel on the Export page turns generic placed shapes into a real order. Every unit you placed appears grouped by type, and each one gets a dropdown of actual brands and models drawn from a 137-model database spanning Rotary, Forward, Direct Lift, Corghi, and Hunter.",
      },
      { type: "h2", text: "Per-unit options" },
      {
        type: "list",
        items: [
          "Brand and model dropdown — model names become clickable links to the manufacturer's product page, on screen and in the PDF",
          "Heavy Duty / Light Duty toggle for tire changers, wheel balancers, and aligners, independent of the brand chosen",
          "Free-text spec fields where there is no fixed catalog — HP, tank size and type for air compressors; a notes field for workbenches and tool cabinets",
        ],
      },
      { type: "h2", text: "Adding it to the PDF" },
      {
        type: "p",
        text: "The models you pick fill the Model column of the equipment schedule in the PDF, linked to the manufacturer's page. Untick Include schedule in PDF to leave the schedule out. The panel itself collapses if you do not need it in the way.",
      },
      {
        type: "info",
        title: "Choices are saved with the project",
        text: "Brand, model, duty rating, and spec text are all stored in the .sbpro file and covered by undo, so a project reopened next week comes back with its order intact.",
      },
    ],
  },
  {
    id: "order-summary",
    category: "Exporting",
    title: "AI order summary",
    lastUpdated: "September 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "Generate Summary writes a plain-language description of what is being ordered, plus a short list of things worth double-checking before installation day — ceiling height, bay depth, and anchoring requirements. Tick Add as a sheet in the PDF to include it as its own sheet.",
      },
      { type: "h2", text: "How it decides what to flag" },
      {
        type: "p",
        text: "The comparisons are done in plain code before the AI is involved — the site conditions you entered are checked against the equipment you placed, and the AI only writes those findings up in readable prose. It is not left to guess whether a 14 ft ceiling clears a given lift.",
      },
      {
        type: "info",
        title: "Fill in site conditions first",
        text: "With ceiling height, concrete thickness, and concrete condition blank there is nothing to check against, and the summary will be thin. Enter them in the Site Conditions panel before generating.",
      },
      {
        type: "warn",
        title: "Read it before you send it",
        text: "The summary is written by a language model from your data. It requires sign-in, it can phrase things awkwardly, and it is not an engineering sign-off.",
      },
    ],
  },
  {
    id: "pdf-export",
    category: "Exporting",
    title: "What is on the PDF drawing",
    lastUpdated: "October 2026",
    readMins: 2,
    sections: [
      {
        type: "p",
        text: "The PDF is a set of drawing sheets. Sheet 1 is the floor plan; the full equipment schedule follows on the next sheet, carrying onto more sheets for big projects.",
      },
      { type: "h2", text: "What is included" },
      {
        type: "list",
        items: [
          "Header with the drawing number, revision, date and shop size",
          "Floor plan with walls, doors, rooms, dimensions, a north arrow and a scale bar",
          "Equipment legend and general notes beside the plan",
          "Title block — project, customer, location, prepared by, floor area, equipment and door counts, site conditions, and sheet X of Y",
          "Equipment schedule — grouped items with quantity, model, footprint and notes",
          "Optional: your logo in the header, and the AI order summary as its own sheet",
        ],
      },
      {
        type: "info",
        title: "Choose what shows",
        text: "The Show on Drawing checkboxes turn names, tags, dimensions, the 5 ft grid, clearance zones, the schedule, notes, title block and site conditions on or off. Your choices, drawing number and revision are saved with the project.",
      },
    ],
  },
];

// Helper: get articles grouped by category, in CATEGORY_ORDER
export function getArticlesByCategory() {
  const grouped = {};
  CATEGORY_ORDER.forEach((cat) => {
    grouped[cat] = HELP_ARTICLES.filter((a) => a.category === cat);
  });
  return grouped;
}

// Helper: find an article by its id (slug)
export function findArticleById(id) {
  return HELP_ARTICLES.find((a) => a.id === id);
}
